import React, { useState, useEffect, useCallback } from "react"
import axios from "axios"
import { useSelector } from "react-redux"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import config from "config/config"
import {
  FacebookIcon,
  HealthGradeIcon,
  VitalsIcon,
  YelpIcon,
} from "./custom-icons"

import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import IconButton from "@mui/material/IconButton"
import { Public } from "@mui/icons-material"

const PLATFORMS = [
  { name: "Facebook", icon: FacebookIcon },
  { name: "Healthgrade", icon: HealthGradeIcon },
  { name: "Yelp", icon: YelpIcon },
  { name: "Vitals", icon: "" },
]

export default function PlatformReviewDashboard({
  title = "Platform Reviews Count",
  legend = ["Facebook", "Healthgrade", "Yelp", "Vitals"],
  viewMode = "all",
  fromDate = null,
  toDate = null,
}) {
  const mode = useSelector(state => state.Layout.layoutMode)
  const [showDropdown, setShowDropdown] = useState(false)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  console.log(organizations, `isConsolidatedView in PlatformReviewGraph`)
  const userData = useSelector(state => state.Login.userData)

  const [timePeriod, setTimePeriod] = useState(viewMode)
  const [platformCounts, setPlatformCounts] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedPlatforms, setSelectedPlatforms] = useState([
    ...PLATFORMS.map(p => p.name),
  ])
  const open = Boolean(anchorEl)

  const handleMenuOpen = event => setAnchorEl(event.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)

  const togglePlatform = platformName => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformName)) {
        return prev.filter(p => p !== platformName)
      } else {
        return [...prev, platformName]
      }
    })
  }

  const fetchReviews = useCallback(async () => {
    if (!activeOrganization) return

    try {
      setIsLoading(true)

      // Prepare query params
      const params = {
        organizationIds:
          isConsolidatedView && Array.isArray(organizations)
            ? organizations.map(o => o.Organization.id).join(",")
            : activeOrganization?.id,
      }
      if (fromDate && toDate) {
        params.startDate = fromDate
        params.endDate = toDate
      } else if (timePeriod && timePeriod !== "all") {
        params.range = timePeriod
      }

      const response = await axios.get(
        `${config.backendUrl}/user/dashboardStats`,
        {
          params, // use the prepared params object
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      const data = response.data || {} // backend returns review data

      let counts = {}
      PLATFORMS.forEach(p => {
        if (data[p.name]) {
          let total = 0
          Object.values(data[p.name]).forEach(bucket => {
            total += bucket.length || 0
          })
          counts[p.name] = total
        } else {
          counts[p.name] = 0
        }
      })

      setPlatformCounts(counts)
      setIsLoading(false)
    } catch (err) {
      console.error("Error fetching dashboard stats:", err)
      setIsLoading(false)
    }
  }, [
    activeOrganization,
    userData.token,
    timePeriod,
    fromDate,
    toDate,
    isConsolidatedView,
    organizations,
  ])

  useEffect(() => {
    fetchReviews()
  }, [fetchReviews])

  const platforms = Array.isArray(PLATFORMS)
    ? PLATFORMS.map(p => ({
        ...p,
        count: (platformCounts && platformCounts[p.name]) || 0,
      }))
    : []

  const displayedPlatforms =
    Array.isArray(platforms) &&
    Array.isArray(selectedPlatforms) &&
    selectedPlatforms.length > 0
      ? platforms.filter(p => selectedPlatforms.includes(p.name))
      : platforms || []

  // ✅ Calculate dynamic max count from displayed platforms
  const maxCount = Math.max(...displayedPlatforms.map(p => p.count), 1)

  // ✅ Generate dynamic Y-axis labels
  const generateYAxisLabels = max => {
    const steps = 6 // Number of labels on Y-axis
    const labels = []
    for (let i = steps; i >= 0; i--) {
      labels.push(Math.round((max / steps) * i))
    }
    return labels
  }

  const yAxisLabels = generateYAxisLabels(maxCount)

  // Check if custom dates are being used
  const isCustomDateRange = fromDate && toDate

  return (
    <div
      className="br"
      style={{
        maxWidth: "768px",
        margin: "0 auto",
        padding: "24px",
        backgroundColor: mode === "dark" ? "#252F40" : "white",
        fontFamily: "Inter, sans-serif",
        boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
          }}
        >
          {title}
        </h2>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            backgroundColor: mode === "dark" ? "#F3F4F6" : "#F3F4F6",
            borderRadius: "8px",
            padding: "6px",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Only show time period buttons if no custom dates */}
          {!isConsolidatedView && !isCustomDateRange && (
            <>
              {["day", "week", "month", "all"].map(period => (
                <button
                  key={period}
                  onClick={() => setTimePeriod(period)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    fontWeight: timePeriod === period ? 600 : 500,
                    border: "none",
                    backgroundColor:
                      timePeriod === period
                        ? mode === "dark"
                          ? "#374151"
                          : "#fff"
                        : "transparent",
                    color:
                      timePeriod === period
                        ? mode === "dark"
                          ? "#fff"
                          : "#111827"
                        : mode === "dark"
                          ? "#9CA3AF"
                          : "#4B5563",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </>
          )}

          {/* Show custom date range label if dates are provided */}
          {isCustomDateRange && (
            <div
              style={{
                padding: "6px 16px",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              {new Date(fromDate).toLocaleDateString()} -{" "}
              {new Date(toDate).toLocaleDateString()}
            </div>
          )}

          {/* Filter Dropdown Button */}
          <div style={{ position: "relative" }}>
            <button
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "none",
                background: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: mode === "dark" ? "#9CA3AF" : "#6B6B6C",
                cursor: "pointer",
              }}
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <FilterAltOutlinedIcon />
            </button>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: mode === "dark" ? "#1F2937" : "#fff",
                  boxShadow:
                    mode === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.5)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                  borderRadius: 8,
                  padding: "8px",
                  zIndex: 20,
                  minWidth: "160px",
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                }}
              >
                {PLATFORMS.map(platform => (
                  <label
                    key={platform.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "8px",
                      fontSize: "0.875rem",
                      color: mode === "dark" ? "#D1D5DB" : "#111827",
                      borderRadius: "4px",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor =
                        mode === "dark" ? "#374151" : "#F3F4F6"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.name)}
                      onChange={() => togglePlatform(platform.name)}
                      style={{ cursor: "pointer" }}
                    />
                    {platform.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform Stats */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        {displayedPlatforms.map((platform, i) => {
          const IconComponent = platform.icon

          return (
            <div
              key={i}
              style={{
                flex: 1, // ensures equal width
                display: "flex",
                flexDirection: "row", // icon left, count right
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "12px",
                padding: "16px",
                borderRadius: "8px",
                minHeight: "80px",
                minWidth: "40px",
                // ensure equal height
                boxShadow:
                  mode === "dark"
                    ? "0px 5px 14px 0px rgba(0,0,0,0.5)"
                    : "0px 5px 14px 0px #E5E7EB",
                backgroundColor: mode === "dark" ? "#252F40" : "#fff",
              }}
            >
              {platform.name.toLowerCase() === "vitals" ? (
                <img
                  src={
                    mode === "dark"
                      ? `/logos/vital-dark.png`
                      : `/logos/vital-light.png`
                  }
                  alt="Vitals"
                  width={32}
                  height={32}
                />
              ) : (
                <IconComponent mode={mode} width={32} height={32} />
              )}

              <div>
                <div
                  style={{
                    fontSize: "1.875rem",
                    fontWeight: "bold",
                    color: mode === "dark" ? "#fff" : "#1F2937",
                  }}
                >
                  {isLoading ? "…" : platform.count}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#94A3B8" : "#6B7280",
                  }}
                >
                  {platform.name}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Chart */}
      <div
        style={{ position: "relative", height: "16rem", paddingTop: "16px" }}
      >
        {/* Y-axis - DYNAMIC */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: "32px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            width: "32px",
          }}
        >
          {yAxisLabels.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>

        {/* Chart area */}
        <div
          style={{
            marginLeft: "40px",
            height: "100%",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
            gap: "48px",
            borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#D1D5DB"}`,
            borderLeft: `1px solid ${mode === "dark" ? "#374151" : "#D1D5DB"}`,
          }}
        >
          {displayedPlatforms.map((platform, i) => {
            const barHeight =
              maxCount > 0 ? (platform.count / maxCount) * 100 : 0
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    height: "14rem",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "6rem",
                      backgroundColor: mode === "dark" ? "#3B82F6" : "#2990DF",
                      borderTopLeftRadius: "4px",
                      cursor: "pointer",
                      borderTopRightRadius: "4px",
                      transition: "all 0.3s ease",
                      height: `${barHeight}%`,
                      minHeight: platform.count > 0 ? "4px" : "0",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.opacity = "0.8"
                      e.currentTarget.style.transform = "scaleY(1.02)"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.opacity = "1"
                      e.currentTarget.style.transform = "scaleY(1)"
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#D1D5DB" : "#374151",
                    marginTop: "12px",
                    fontWeight: 500,
                  }}
                >
                  {platform.name}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
